import React, { useEffect, useState } from 'react';
import { Button, Select, SelectOption } from '@flixbus/honeycomb-react';
import { Icon, IconArrowLeftL, IconArrowRightL } from '@flixbus/honeycomb-icons-react';

import styles from './pagination.module.scss';

type PaginationProps = {
  totalPages: number;
  currentPage: number;
  // eslint-disable-next-line no-unused-vars
  onPageChange?: (page: number) => void;
  loading?: boolean;
};

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
  loading,
}) => {
  const [selectedPage, setSelectedPage] = useState(currentPage);

  useEffect(() => {
    setSelectedPage(currentPage);
  }, [currentPage]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange && onPageChange(page);
    }
  };

  const getPages = () => {
    const maxVisiblePages = 20;
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pages.push(1, '...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className={styles.container}>
      <div className={styles.buttongroup}>
        <Button
          extraClasses={styles.button}
          disabled={currentPage === 1 || loading}
          onClick={() => {
            setSelectedPage(currentPage - 1);
            goToPage(currentPage - 1);
          }}
        >
          <Icon InlineIcon={IconArrowLeftL} />
        </Button>
      </div>

      <div className={styles.pagination}>
        Page
        <Select
          id='selectPage'
          aria-label='Select Page'
          value={selectedPage}
          onChange={e => {
            const selected = Number(e.target.value);
            setSelectedPage(selected);
            goToPage(selected);
          }}
        >
          {getPages().map((page, index) => (
            <SelectOption
              key={index}
              value={typeof page === 'number' ? page : ''}
              disabled={typeof page !== 'number'}
            >
              {page}
            </SelectOption>
          ))}
        </Select>
      </div>

      <div className={styles.buttongroup}>
        <Button
          extraClasses={styles.button}
          disabled={currentPage === totalPages || loading}
          onClick={() => {
            setSelectedPage(currentPage + 1);
            goToPage(currentPage + 1);
          }}
        >
          <Icon InlineIcon={IconArrowRightL} />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
